import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  AlertTriangle,
  TrendingDown,
  FolderGit2,
  ArrowRight,
  XCircle,
  CheckCircle2,
  GitBranch,
  Upload,
  RefreshCw,
  Clock,
} from "lucide-react";

import Layout, { PageHeader, PageContainer } from "../components/ui/Layout";
import { useRepos, useSyncRepos } from "../api/hooks";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Separator } from "../components/shadcn/separator";
import { Skeleton } from "../components/shadcn/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/shadcn/tooltip";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/shadcn/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/shadcn/dialog";

// ─── Severity Distribution Bar ──────────────────────────────────────

function SeverityBar({
  critical,
  high,
  medium,
  low,
  total,
}: {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}) {
  if (total === 0) {
    return (
      <div className="h-3 rounded-full bg-green-500/20 overflow-hidden">
        <div className="h-full bg-green-500/60 rounded-full w-full" />
      </div>
    );
  }
  const pct = (v: number) => Math.max(v > 0 ? 2 : 0, (v / total) * 100);

  const segments = [
    { key: "critical", value: critical, color: "bg-red-500", label: "Critical" },
    { key: "high", value: high, color: "bg-orange-500", label: "High" },
    { key: "medium", value: medium, color: "bg-yellow-500", label: "Medium" },
    { key: "low", value: low, color: "bg-blue-500", label: "Low" },
  ];

  return (
    <TooltipProvider>
      <div className="h-3 rounded-full bg-muted overflow-hidden flex">
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <Tooltip key={s.key}>
              <TooltipTrigger asChild>
                <div
                  className={cn("h-full transition-all duration-700", s.color)}
                  style={{ width: `${pct(s.value)}%` }}
                />
              </TooltipTrigger>
              <TooltipContent>
                {s.label}: {s.value}
              </TooltipContent>
            </Tooltip>
          ))}
      </div>
    </TooltipProvider>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  accentClass,
  subtitle,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accentClass?: string;
  subtitle?: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
          <div className="text-muted-foreground">{icon}</div>
        </div>
        <div className={cn("text-3xl font-bold", accentClass)}>
          {value.toLocaleString()}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Loading Skeleton ───────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      {/* Stats row skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-5 rounded" />
              </div>
              <Skeleton className="h-9 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Severity bar skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-3 w-full rounded-full" />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <Skeleton className="h-3 w-3 rounded-full" />
                <div>
                  <Skeleton className="h-6 w-10" />
                  <Skeleton className="h-3 w-14 mt-1" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Table skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 py-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Error State ────────────────────────────────────────────────────

function DashboardError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card className="text-center py-12">
      <CardContent className="flex flex-col items-center gap-4 pt-6">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/10 text-destructive ring-1 ring-destructive/20">
          <XCircle className="h-7 w-7" />
        </div>
        <div className="text-lg font-semibold">Failed to load dashboard</div>
        <p className="text-sm text-muted-foreground max-w-md">
          {message || "Unable to fetch repository data. Please try again."}
        </p>
        <Button onClick={onRetry} className="mt-2">
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Repos Table ────────────────────────────────────────────────────

function ReposTable({
  repos,
  user,
  onConnectGithub,
}: {
  repos: Array<{
    id: string;
    url: string;
    open_count?: number;
    open_critical?: number;
    open_high?: number;
    open_medium?: number;
    open_low?: number;
    last_scan_at?: string;
    last_scan_status?: string;
  }>;
  user: any;
  onConnectGithub: () => void;
}) {
  if (repos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Repositories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-green-500/10 text-green-500 ring-1 ring-green-500/20 mb-4">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="text-lg font-semibold">Let's scan your first repository</div>
            <p className="mx-auto mt-1 mb-4 max-w-sm text-sm text-muted-foreground">
              Paste a public repository URL or upload your code as a zip — no
              GitHub connection required. You can also connect GitHub for
              automatic scans on every pull request.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild>
                <Link to="/upload">
                  <Upload className="mr-2 h-4 w-4" />
                  Scan a repository
                </Link>
              </Button>
              {!user?.github_login ? (
                <Button variant="outline" onClick={onConnectGithub}>Connect GitHub</Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Top Repositories by Open Findings</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Repository</TableHead>
              <TableHead className="text-center">Open</TableHead>
              <TableHead className="text-center">Critical</TableHead>
              <TableHead className="text-center">High</TableHead>
              <TableHead>Last Scan</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {repos.map((repo) => {
              const repoName = repo.url.replace("https://github.com/", "");
              const openCount = repo.open_count || 0;
              const critCount = repo.open_critical || 0;
              const highCount = repo.open_high || 0;
              const lastScan = repo.last_scan_at
                ? new Date(repo.last_scan_at).toLocaleDateString()
                : "Never";
              const status = repo.last_scan_status || "unknown";

              return (
                <TableRow key={repo.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/dashboard/repos/${repo.id}`}
                      className="hover:underline text-foreground hover:text-primary transition-colors"
                    >
                      {repoName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={openCount > 0 ? "secondary" : "outline"}>
                      {openCount}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {critCount > 0 ? (
                      <Badge variant="critical">{critCount}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {highCount > 0 ? (
                      <Badge variant="high">{highCount}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">0</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {lastScan}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        status === "ok"
                          ? "secondary"
                          : status === "error"
                            ? "destructive"
                            : "outline"
                      }
                    >
                      {status === "ok"
                        ? "Healthy"
                        : status === "partial"
                          ? "Partial"
                          : status === "error"
                            ? "Error"
                            : "Unknown"}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Quick Actions ──────────────────────────────────────────────────

function QuickActions({ onSync, isSyncing }: { onSync: () => void; isSyncing: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/upload">
            <Upload className="h-4 w-4 mr-2" />
            Scan a repository
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard/repos">
            <GitBranch className="h-4 w-4 mr-2" />
            View All Repos
          </Link>
        </Button>
        <Button variant="outline" onClick={onSync} disabled={isSyncing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isSyncing && "animate-spin")} />
          {isSyncing ? "Syncing..." : "Sync from GitHub"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────

export default function Dashboard() {
  const { data: repos = [], isLoading, isError, error, refetch } = useRepos();
  const syncRepos = useSyncRepos();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [showConnectModal, setShowConnectModal] = useState(false);

  useEffect(() => {
    if (user && !user.github_login && !sessionStorage.getItem("githubConnectModalShown")) {
      setShowConnectModal(true);
    }
  }, [user]);

  const handleCloseModal = () => {
    setShowConnectModal(false);
    sessionStorage.setItem("githubConnectModalShown", "true");
  };

  // Computed stats -- preserve all existing logic
  const stats = React.useMemo(() => {
    let critical = 0,
      high = 0,
      medium = 0,
      low = 0,
      open = 0,
      fixed = 0;

    repos.forEach((repo) => {
      critical += repo.open_critical || 0;
      high += repo.open_high || 0;
      medium += repo.open_medium || 0;
      low += repo.open_low || 0;
      open += repo.open_count || 0;
      fixed += repo.fixed_count || 0;
    });

    const riskScore = Math.min(
      100,
      Math.round(critical * 25 + high * 10 + medium * 3 + low * 1)
    );

    return { critical, high, medium, low, open, fixed, riskScore, repoCount: repos.length };
  }, [repos]);

  const topRiskyRepos = React.useMemo(() => {
    return [...repos]
      .sort((a, b) => {
        const scoreA =
          (a.open_critical || 0) * 100 +
          (a.open_high || 0) * 10 +
          (a.open_medium || 0);
        const scoreB =
          (b.open_critical || 0) * 100 +
          (b.open_high || 0) * 10 +
          (b.open_medium || 0);
        return scoreB - scoreA;
      })
      .slice(0, 10);
  }, [repos]);

  return (
    <Layout>
      <PageContainer>
        <PageHeader title="Dashboard" description="Security overview" />

        {isLoading ? (
          <DashboardSkeleton />
        ) : isError ? (
          <DashboardError
            message={(error as Error)?.message || ""}
            onRetry={() => refetch()}
          />
        ) : (
          <div className="space-y-6">
            {/* ── Stats Row ─────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Open"
                value={stats.open}
                icon={<Shield className="h-5 w-5" />}
                subtitle={
                  stats.open === 0
                    ? "No open findings"
                    : `Across ${stats.repoCount} repositories`
                }
              />
              <StatCard
                label="Critical"
                value={stats.critical}
                icon={<AlertTriangle className="h-5 w-5" />}
                accentClass="text-red-500"
                subtitle={
                  stats.critical === 0
                    ? "No critical issues"
                    : "Requires immediate attention"
                }
              />
              <StatCard
                label="Total Fixed"
                value={stats.fixed}
                icon={<TrendingDown className="h-5 w-5" />}
                accentClass="text-green-500"
                subtitle="Resolved across all repos"
              />
              <StatCard
                label="Repositories"
                value={stats.repoCount}
                icon={<FolderGit2 className="h-5 w-5" />}
                subtitle="Protected by Paxley"
              />
            </div>

            {/* ── Severity Distribution ─────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Severity Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <SeverityBar
                  critical={stats.critical}
                  high={stats.high}
                  medium={stats.medium}
                  low={stats.low}
                  total={stats.open}
                />

                <Separator className="my-5" />

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { label: "Critical", value: stats.critical, color: "text-red-500", dot: "bg-red-500" },
                    { label: "High", value: stats.high, color: "text-orange-500", dot: "bg-orange-500" },
                    { label: "Medium", value: stats.medium, color: "text-yellow-500", dot: "bg-yellow-500" },
                    { label: "Low", value: stats.low, color: "text-blue-500", dot: "bg-blue-500" },
                    { label: "Total Open", value: stats.open, color: "text-foreground", dot: "bg-muted-foreground" },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-2.5">
                      <div className={cn("h-2.5 w-2.5 rounded-full", s.dot)} />
                      <div>
                        <div className={cn("text-xl font-bold", s.color)}>
                          {s.value.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ── Repositories Table ───────────────────────── */}
            <ReposTable repos={topRiskyRepos} user={user} onConnectGithub={login} />

            {/* ── Quick Actions ────────────────────────────── */}
            <QuickActions
              onSync={() => syncRepos.mutate()}
              isSyncing={syncRepos.isPending}
            />
          </div>
        )}

        <Dialog open={showConnectModal} onOpenChange={(open) => {
          if (!open) handleCloseModal();
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Connect GitHub or Scan Locally</DialogTitle>
              <DialogDescription>
                Connect your GitHub account to automatically analyze all of your repositories, or use the Manual Code Scan if you prefer to upload your code locally.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col items-center justify-center p-6 border rounded-lg bg-muted/20 gap-3">
                <FolderGit2 className="h-8 w-8 text-muted-foreground" />
                <div className="text-center">
                  <h3 className="font-medium">Automatic Integration</h3>
                  <p className="text-sm text-muted-foreground mb-4">Continuously scan your repos on every commit.</p>
                  <Button onClick={() => { handleCloseModal(); login(); }} className="w-full">
                    <GitBranch className="mr-2 h-4 w-4" />
                    Connect GitHub
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center p-6 border rounded-lg bg-muted/20 gap-3">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <div className="text-center">
                  <h3 className="font-medium">Manual Code Scan</h3>
                  <p className="text-sm text-muted-foreground mb-4">Upload a local ZIP or TAR.GZ archive to scan.</p>
                  <Button variant="outline" onClick={() => { handleCloseModal(); navigate("/upload"); }} className="w-full">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Code
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </Layout>
  );
}
